import React, { CSSProperties, FC } from "react";
import { forwardRef, useImperativeHandle } from "react";
import moment from "moment";
import { Form, Input, Button, Select, Switch, DatePicker, InputNumber, Row, Col, Radio, Checkbox } from "antd";
import { useForm, FormInstance, FormProps } from "antd/lib/form/Form";
import { Rule } from "antd/lib/form";
import { ImageCrop } from "@/components/ImageCrop";
import { NamePath } from "antd/lib/form/interface";
import { castArray } from "lodash";
import { SelectProps } from "antd/lib/select";
import { ColProps } from "antd/lib/col";
import { RowProps } from "antd/lib/row";
import classnames from "classnames";
import styles from "./styles.module.less";
import { PickerProps } from "antd/es/date-picker/generatePicker";
import { i18n } from "@/locales";

export type FieldType =
  | "text"
  | "textarea"
  | "select"
  | "checkbox"
  | "radio"
  | "logo"
  | "password"
  | "switch"
  | "number"
  | "datetime"
  | "custom";

export interface FieldConfig {
  type: FieldType;
  label?: React.ReactNode;
  name?: NamePath;
  // 唯一 key，react 渲染用
  key?: string | number;
  help?: React.ReactNode;
  tooltip?: React.ReactNode;
  extra?: React.ReactNode;
  placeholder?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  value?: any;
  rules?: Rule[];
  valuePropName?: string;
  maxLength?: number;
  // InputNumber 的 min 和 max
  min?: number;
  max?: number;
  // select or radio or checkbox 的 options
  optionsWithRowLayout?: boolean;
  options?: {
    label?: React.ReactNode;
    value?: string | number | boolean;
    options?: any;
    optionCol?: ColProps;
    [index: string]: any;
  }[];
  // select 的 allowClear
  allowClear?: boolean;
  // select 的 mode
  mode?: SelectProps<any>["mode"];
  // textarea 的 minLength
  minLength?: number;
  // textarea 的 rows
  rows?: number;
  readOnly?: boolean;
  disabled?: boolean;
  // ImageCrop 的 size
  size?: string | number;
  hidden?: boolean;

  // datePicker 的 format
  format?: PickerProps<moment.Moment>["format"];

  // 日期在 plain mode 下的 format
  datePlainFormat?: string;

  fieldCol?: ColProps;

  style?: CSSProperties;

  // 自定义组件
  customComponent?: React.ReactNode;
  // 转换为 plain 模式时的转换方式
  toPlainText?: (value?: any) => React.ReactNode;
}

export type FormMode = "edit" | "plain";

export const PlainText: FC<{
  value?: any;
  datePlainFormat?: string;
  type: FieldType;
  options?: FieldConfig["options"];
  toPlainText?: FieldConfig["toPlainText"];
  /**
   * @deprecated 配置写死的 value，不再用了
   */
  configValue?: any;
}> = ({ toPlainText, value, datePlainFormat, type, options, configValue }) => {
  value = configValue ?? value;
  let text = value ?? "-";

  if (toPlainText) {
    text = toPlainText(value);
  } else if (typeof value === "boolean") {
    text = String(value) === "true" ? i18n.t("common.yes") : i18n.t("common.no");
  } else if (moment.isDate(value) || moment.isMoment(value)) {
    text = moment(value).format(datePlainFormat);
  } else if (["select", "radio"].includes(type)) {
    text = options?.find((item) => item.value === value)?.label || value;
  }

  return <div className={classnames("plain-form-field-item", styles.plainItem)}>{text}</div>;
};

export interface SimpleFromProps extends FormProps {
  fieldConfig: FieldConfig[];
  footer?: React.ReactNode;
  mode?: FormMode;
  useRowLayout?: boolean;
  fieldCol?: ColProps;
  rowProps?: RowProps;
}

// 传入 json 生成简单的表单
export const SimpleForm = forwardRef<FormInstance, SimpleFromProps>(
  ({ fieldConfig, footer, useRowLayout, mode = "edit", fieldCol, rowProps, children, ...formProps }, ref) => {
    const [rawForm] = useForm();

    useImperativeHandle(ref, () => rawForm);

    const getFieldComponent = (config: FieldConfig) => {
      switch (config.type) {
        case "text":
          return (
            <Input
              addonBefore={config.addonBefore}
              addonAfter={config.addonAfter}
              prefix={config.prefix}
              suffix={config.suffix}
              disabled={config.disabled}
              placeholder={config.placeholder}
              readOnly={config.readOnly}
              style={{ ...config.style }}
              maxLength={config.maxLength}
            />
          );
        case "password":
          return (
            <Input.Password
              addonBefore={config.addonBefore}
              addonAfter={config.addonAfter}
              prefix={config.prefix}
              suffix={config.suffix}
              disabled={config.disabled}
              placeholder={config.placeholder}
              readOnly={config.readOnly}
              style={{ ...config.style }}
              maxLength={config.maxLength}
            />
          );

        case "textarea":
          return (
            <Input.TextArea
              prefix={config.prefix as string}
              disabled={config.disabled}
              rows={config.rows}
              maxLength={config.maxLength}
              minLength={config.minLength}
              readOnly={config.readOnly}
              placeholder={config.placeholder}
              style={{ ...config.style }}
            />
          );

        case "select":
          return (
            <Select
              prefix={config.prefix}
              suffix={config.suffix}
              mode={config.mode}
              disabled={config.disabled}
              placeholder={config.placeholder}
              // @ts-ignore
              options={config.options}
              allowClear={config.allowClear}
              style={{ ...config.style }}
            />
          );

        case "radio":
          return config.optionsWithRowLayout ? (
            <Radio.Group disabled={config.disabled} style={{ width: "100%", ...config.style }}>
              <Row
                gutter={[0, 20]}
                style={{
                  marginBottom: -10,
                }}
              >
                {config.options?.map((opt) => {
                  return (
                    <Col key={String(opt.value)} {...opt.optionCol}>
                      <Radio value={opt.value}>{opt.label}</Radio>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          ) : (
            <Radio.Group
              disabled={config.disabled}
              // @ts-ignore
              options={config.options}
              style={{ ...config.style }}
            ></Radio.Group>
          );

        case "checkbox":
          return config.optionsWithRowLayout ? (
            <Checkbox.Group disabled={config.disabled} style={{ width: "100%", ...config.style }}>
              <Row
                gutter={[0, 20]}
                style={{
                  marginBottom: -10,
                }}
              >
                {config.options?.map((opt) => {
                  return (
                    <Col key={String(opt.value)} {...opt.optionCol}>
                      <Checkbox value={opt.value}>{opt.label}</Checkbox>
                    </Col>
                  );
                })}
              </Row>
            </Checkbox.Group>
          ) : (
            <Checkbox.Group
              disabled={config.disabled}
              // @ts-ignore
              options={config.options}
              style={{ ...config.style }}
            ></Checkbox.Group>
          );

        case "switch":
          return <Switch style={{ ...config.style }} disabled={config.disabled} />;

        case "logo":
          return <ImageCrop size={config.size} />;

        case "datetime":
          return (
            <DatePicker
              disabled={config.disabled}
              placeholder={config.placeholder}
              allowClear={config.allowClear}
              style={{ ...config.style }}
              format={config.format}
            />
          );

        case "number":
          return (
            <InputNumber
              max={config.max}
              min={config.min}
              prefix={config.prefix as string}
              disabled={config.disabled}
              placeholder={config.placeholder}
              style={{ ...config.style }}
            />
          );
        case "custom":
          return config.customComponent;

        default:
          return null;
      }
    };

    const Fields = fieldConfig.map((item, index) => {
      const itemKey = item.key || castArray(item.name).join("") || index;

      const FieldItem = (
        <Form.Item
          extra={item.extra}
          hidden={item.hidden}
          key={itemKey}
          rules={item.rules}
          label={item.label}
          name={item.name}
          help={item.help}
          tooltip={item.tooltip}
          valuePropName={item.type === "switch" ? "checked" : item.valuePropName || "value"}
          className={`simple-form-item-${item.type}`}
        >
          {mode === "edit" ? (
            getFieldComponent(item)
          ) : (
            <PlainText
              configValue={item.value}
              datePlainFormat={item.datePlainFormat}
              type={item.type}
              options={item.options}
              toPlainText={item.toPlainText}
            />
          )}
        </Form.Item>
      );

      return useRowLayout ? (
        <Col key={itemKey} {...(fieldCol || item.fieldCol)}>
          {FieldItem}
        </Col>
      ) : (
        FieldItem
      );
    });

    return (
      <Form form={rawForm} {...formProps} scrollToFirstError={true}>
        {useRowLayout ? <Row {...rowProps}>{Fields}</Row> : Fields}
        {children}
        {footer ? footer : <Button hidden htmlType="submit"></Button>}
      </Form>
    );
  }
);
